<template>
  <div class="p-4 w-full">
    <!-- Header -->
    <div class="mb-6 d-flex justify-content-between align-items-center">
      <h1 class="text-xl md:text-2xl lg:text-3xl font-bold mb-4 text-white">
        {{ $t("user_progression.title") }}
      </h1>
      <div class="flex gap-2">
        <SplitButton
          :model="items"
          icon="pi pi-download"
          :label="$t('common.download_report') || 'Rapport (PDF)'"
          :loading="isDownloading"
          class="mb-4 text-sm rounded-full w-full md:w-auto"
        />
        <Button
          icon="pi pi-refresh"
          :label="$t('common.refresh')"
          @click="refreshData(true)"
          :loading="loading"
          class="mb-4 text-sm rounded-full spect-btn-primary w-full md:w-auto"
        />
      </div>
    </div>

    <div class="flex flex-wrap gap-2 mb-8">
      <Button
        asChild
        v-for="tab in visibleTabs"
        :key="tab.label"
        custom
        class="w-full sm:w-auto"
      >
        <router-link
          :to="tab.route"
          :class="[
            'px-3 md:px-6 py-2 font-semibold rounded-full transition flex items-center gap-2 text-sm md:text-base w-full justify-center sm:w-auto',
            tab.route == activeTab
              ? 'bg-white primary-text shadow'
              : 'text-white bg-transparent hover:bg-white/20',
          ]"
        >
          <span>{{ tab.label }}</span>
        </router-link>
      </Button>
    </div>

    <!-- Statistics Cards -->
    <FollowStatsCards v-if="!loading && activeTab !== '/user-follow#stats'" :followStats="displayedStats" />

    <!-- Search Field -->
    <div class="mt-24 mb-4" v-if="activeTab !== '/user-follow#stats'">
      <InputText
        v-model="searchTerm"
        :placeholder="$t('common.search_placeholder')"
        class="w-full md:w-1/3"
      />
    </div>

    <!-- User Follow Data Table -->
    <Card>
      <!-- <template #header>
        <div class="p-4">
          <h2 class="text-lg font-semibold">{{ $t('user_progression.user_list') }}</h2>
        </div>
      </template> -->

      <template #content v-if="activeTab !== '/user-follow#stats'">
        <UserFollowDataTable
          :users="filteredUsers"
          :loading="loading"
          :isPluzz="activeTab === '/user-follow#pluzz'"
        />
      </template>

      <template #content v-else>
        <StatisticsByEntity />
      </template>
    </Card>

    <!-- Global Components -->
    <Toast />
  </div>
</template>

<script setup>
/* eslint-disable */
import { useI18n } from "vue-i18n";
import { ref, watch, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";

// PrimeVue Components
import Card from "primevue/card";
import Button from "primevue/button";
import SplitButton from "primevue/splitbutton";
import InputText from "primevue/inputtext";
import Toast from "primevue/toast";

// Custom Components
import UserFollowDataTable from "@/components/user-follow/UserFollowDataTable.vue";
import FollowStatsCards from "@/components/user-follow/FollowStatsCards.vue";
import StatisticsByEntity from "@/components/user-follow/StatisticsByEntity.vue";

// Composables
import { useUserFollow } from "@/composables/useUserFollow";

const { t } = useI18n();

// Use composable
const {
  loading,
  users,
  activeTab,
  usersStats,
  modulePluzzStats,
  searchTerm,
  filteredUsers,
  refreshData,
  downloadReport,
  isDownloading,
} = useUserFollow();

const items = [
  {
    label: t("common.download_report_pdf"),
    icon: "pi pi-file-pdf",
    command: async () => {
      if (activeTab.value === "/user-follow")
        await downloadReport("pdf", "awareness");
      else if (activeTab.value === "/user-follow#pluzz")
        await downloadReport("pdf", "pluzz");
    },
  },
  {
    label: t("common.download_report_excel"),
    icon: "pi pi-file-excel",
    command: async () => {
      if (activeTab.value === "/user-follow")
        await downloadReport("excel", "awareness");
      else if (activeTab.value === "/user-follow#pluzz")
        await downloadReport("excel", "pluzz");
    },
  },
  {
    label: t("common.download_report_csv"),
    icon: "pi pi-file-export",
    command: () => {},
  },
];

const route = useRoute();
const router = useRouter();

const hasPluzz = computed(() => {
  return localStorage.getItem("isPluzz") == "true";
});

const hasModule = computed(() => {
  return localStorage.getItem("isModule") == "true";
});

const tabs = ref([
  {
    route: "/user-follow",
    label: `${t("user_progression.tab.label_modules")}`,
    icon: "pi pi-book",
    display: hasModule.value,
  },
  {
    route: "/user-follow#pluzz",
    label: `${t("user_progression.tab.label_modules_pluzz")}`,
    icon: "pi pi-gamepad",
    display: hasPluzz.value,
  },
  {
    route: "/user-follow#stats",
    label: `${t("user_progression.tab.label_stats")}`,
    icon: "pi pi-chart-line",
    display: true,
  }
]);

const visibleTabs = computed(() => {
  return tabs.value.filter((tab) => tab.display !== false);
});

//const activeTab = ref(route.fullPath);

const displayedStats = computed(() => {
  if (activeTab.value === "/user-follow#pluzz") {
    return {
      users_started: modulePluzzStats.value.users_started_pluzz,
      users_over_50: modulePluzzStats.value.users_over_50_pluzz,
      users_less_50: modulePluzzStats.value.users_less_50_pluzz,
      users_completed: modulePluzzStats.value.users_completed_pluzz,
      total_users: modulePluzzStats.value.total_users,
    };
  } else {
    return usersStats.value;
  }
});

// onMounted(() => {
//   if (hasPluzz.value && !hasModule.value) {
//     activeTab.value = '/user-follow#pluzz',
//     router.push('/user-follow#pluzz')
//   } else {
//     activeTab.value = '/user-follow'
//     router.push('/user-follow')
//   }
// })

onMounted(() => {
  if (hasPluzz.value && !hasModule.value) {
    if (route.path === "/user-follow" && !route.hash) {
      router.replace("/user-follow#pluzz");
      activeTab.value = "/user-follow#pluzz";
    } else {
      activeTab.value = "/user-follow";
      router.replace("/user-follow");
    }
  }
});

watch(
  () => route.fullPath,
  (newPath) => {
    activeTab.value = newPath;
  }
);
</script>

<style lang="scss" scoped>
@use "../../../assets/scss/variables.scss";
@use "../../../assets/scss/app/common.scss";

.stat-card {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
}

.primary-text {
  color: #1f2937;
}

.d-flex {
  display: flex;
}

.justify-content-between {
  justify-content: space-between;
}

.align-items-center {
  align-items: center;
}
</style>
